import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { NOTIFICATION_TYPES, NOTIFICATION_DURATION, NotificationType } from '../utils/constants';

export interface Notification {
  id: string;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  timestamp: Date;
}

interface NotificationState {
  // Lista de notificações ativas
  notifications: Notification[];
  
  // Configurações
  maxNotifications: number;
  defaultDuration: number;
  position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  
  // Ações básicas
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  
  // Ações específicas por tipo
  success: (message: string, title?: string, duration?: number) => string;
  error: (message: string, title?: string, duration?: number) => string;
  warning: (message: string, title?: string, duration?: number) => string;
  info: (message: string, title?: string, duration?: number) => string;
  
  // Configurações
  setPosition: (position: NotificationState['position']) => void;
  setMaxNotifications: (max: number) => void;
  setDefaultDuration: (duration: number) => void;
}

const useNotificationStore = create<NotificationState>()(
  devtools(
    (set, get) => ({
      notifications: [],
      maxNotifications: 5,
      defaultDuration: NOTIFICATION_DURATION.MEDIUM,
      position: 'top-right',
      
      addNotification: (notification) => {
        const id = `notification-${Date.now()}-${Math.random()}`;
        const newNotification: Notification = {
          ...notification,
          id,
          timestamp: new Date(),
          duration: notification.duration ?? get().defaultDuration,
        };
        
        set((state) => {
          let notifications = [...state.notifications, newNotification];
          
          // Limitar número de notificações
          if (notifications.length > state.maxNotifications) {
            notifications = notifications.slice(-state.maxNotifications);
          }
          
          return { notifications };
        });
        
        // Auto-remover após a duração especificada
        if (newNotification.duration && newNotification.duration > 0) {
          setTimeout(() => {
            get().removeNotification(id);
          }, newNotification.duration);
        }
        
        return id;
      },
      
      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },
      
      clearAllNotifications: () => {
        set({ notifications: [] });
      },
      
      success: (message, title, duration) => {
        return get().addNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          message,
          title: title || 'Sucesso',
          duration: duration ?? NOTIFICATION_DURATION.SHORT,
        });
      },
      
      error: (message, title, duration) => {
        return get().addNotification({
          type: NOTIFICATION_TYPES.ERROR,
          message,
          title: title || 'Erro',
          duration: duration ?? NOTIFICATION_DURATION.LONG,
        });
      },
      
      warning: (message, title, duration) => {
        return get().addNotification({
          type: NOTIFICATION_TYPES.WARNING,
          message,
          title: title || 'Atenção',
          duration: duration ?? NOTIFICATION_DURATION.MEDIUM,
        });
      },
      
      info: (message, title, duration) => {
        return get().addNotification({
          type: NOTIFICATION_TYPES.INFO,
          message,
          title: title || 'Informação',
          duration: duration ?? NOTIFICATION_DURATION.MEDIUM,
        });
      },
      
      setPosition: (position) => {
        set({ position });
      },
      
      setMaxNotifications: (max) => {
        set({ maxNotifications: max });
      },
      
      setDefaultDuration: (duration) => {
        set({ defaultDuration: duration });
      },
    }),
    {
      name: 'notification-store',
    }
  )
);

// Hook helper para usar notificações
export const useNotifications = () => {
  const { success, error, warning, info, addNotification } = useNotificationStore();
  
  return {
    success,
    error,
    warning,
    info,
    notify: addNotification,
  };
};

export default useNotificationStore;